import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  CreditCardIcon, 
  PlayIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  SettingsIcon,
  VolumeIcon,
  VolumeXIcon,
  RotateCcwIcon,
  TrophyIcon,
  ClockIcon,
  TargetIcon,
  ZapIcon,
  BarChart3Icon,
  AwardIcon,
  ActivityIcon
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import SorobanDisplay from '../../components/common/SorobanDisplay';
import { useStudentFocusSession } from '../../contexts/StudentFocusSessionContext';
import FullscreenNumberDisplay, { FlashTickOverlay } from '../../components/student/FullscreenNumberDisplay';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';

const kidFieldLabel =
  'mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500 sm:text-xs';
const kidInputClass =
  'h-9 w-full rounded-xl border-2 border-sky-200 bg-white px-2.5 text-xs font-semibold text-slate-800 shadow-inner transition focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200/80 sm:text-sm';

const FlashCards = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { isRTL } = useLanguage();

  const studentLevelNumber = Number(user?.level?.levelNumber);
  const flashCardsSorobanOnlyByLevel =
    Number.isFinite(studentLevelNumber) && studentLevelNumber >= 2;

  // إعدادات الجلسة
  const [sessionSettings, setSessionSettings] = useState({
    totalCards: '5',
    digitsPerNumber: '1',
    estimatedTimePerCard: '3000',
    totalRounds: '3',
    displayType: 'multipleChoice' // 'multipleChoice', 'text', 'soroban'
  });

  // حالة شاشة اللعب
  const [gameState, setGameState] = useState('settings'); // 'settings', 'ready', 'playing', 'result', 'roundComplete'
  const [sessionData, setSessionData] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [answers, setAnswers] = useState([]); // تجميع الإجابات محلياً
  const [studentAnswer, setStudentAnswer] = useState(''); // للإدخال النصي
  const [cardStartTime, setCardStartTime] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // حالة الجولات المتتالية
  const [currentRound, setCurrentRound] = useState(1);
  const [roundResults, setRoundResults] = useState([]);
  const [allSessionsData, setAllSessionsData] = useState([]);

  // حالة الإحصائيات العامة
  const [overallStats, setOverallStats] = useState({
    totalSessions: 0,
    totalCorrectAnswers: 0,
    totalCardsAttempted: 0,
    avgScore: 0,
    avgAccuracy: 0,
    avgTime: 0,
    totalScore: 0
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // حالة جدول الأرشيف
  const [archiveData, setArchiveData] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [archivePagination, setArchivePagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  // حالة المعداد التفاعلي
  const [abacus, setAbacus] = useState(null);
  const [abacusValue, setAbacusValue] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showNumbers, setShowNumbers] = useState(true);

  // Refs للتحكم في الأصوات والمعداد
  const audioContextRef = useRef(null);
  const canvasRef = useRef(null);
  const audioRef = useRef(null);

  const { setFocusSession } = useStudentFocusSession();

  const playBeepSound = useCallback(() => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      oscillator.frequency.setValueAtTime(800, audioContextRef.current.currentTime);
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.1);
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 0.1);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    setFocusSession(gameState !== 'settings');
    return () => setFocusSession(false);
  }, [gameState, setFocusSession]);

  /** من المستوى 2 فما فوق: نوع العرض يبقى السوروبان فقط (الخيار الثالث) */
  useEffect(() => {
    if (!flashCardsSorobanOnlyByLevel) return;
    setSessionSettings((prev) =>
      prev.displayType === 'soroban' ? prev : { ...prev, displayType: 'soroban' }
    );
  }, [flashCardsSorobanOnlyByLevel]);

  useEffect(() => {
    if (gameState !== 'playing') return;
    playBeepSound();
  }, [currentCardIndex, gameState, playBeepSound]);

  // Constants للمعداد
  const DISTANCE_RODS = 60;
  const TOP_MARGIN = 60;
  const NUMBER_HEIGHT = 20;
  /** وضع السوروبان بدون أرقام أعمدة: فراغ بسيط متساوٍ فوق وتحت الإطار (بالبكسل داخل الـ canvas) */
  const FRAME_PAD_COMPACT = 8;
  const LEFT_MARGIN = 10;
  const FRAME_LINE_WIDTH = 10;
  const ROD_STROKE_STYLE = 'rgba(212,85,0,0.5)';
  const ROD_LINE_WIDTH = 6;
  const DOT_STROKE_STYLE = 'rgba(0, 0, 0, 1)';
  const DOT_FILL_STYLE = 'rgba(255, 255, 255, 1)';
  const DOT_SIZE = 3;
  const BEAD_WIDTH = 56;
  const BEAD_HEIGHT = 30;
  const BEAD_STROKE = 'black';
  const HEAVEN = BEAD_HEIGHT * 2 + FRAME_LINE_WIDTH;
  const EARTH = BEAD_HEIGHT * 5;
  const HEIGHT = HEAVEN + EARTH + FRAME_LINE_WIDTH;
  const frameColor = '#1E40AF';
  const beadColor = '#90EE90';
  const activeColor = '#228B22';

  // Classes للمعداد
  class AbacusClass {
    constructor(numberOfRods, mode, frameColor, showNumbers) {
      this.rods = [];
      for (let i = 0; i < numberOfRods; i++) {
        const beads = [];
        const rod = new RodClass(i + 1, beads, 0, false);
        for (let j = 0; j < 5; j++) {
          let bead;
          if (j === 0) { 
            bead = new BeadClass(rod, true, j, false);
          } else {
            bead = new BeadClass(rod, false, j, false);
          }
          beads.push(bead);
        }
        this.rods.push(rod);
      }
      this.numberOfRods = numberOfRods;
      this.mode = mode;
      this.frameColor = frameColor;
      this.showNumbers = showNumbers;
      this.middleRod = Math.floor(numberOfRods / 2) + 1;
      this.width = DISTANCE_RODS * (numberOfRods + 1);
    }

    drawFrame(context, topFrame) {
      context.save();
      context.strokeStyle = frameColor;
      context.lineWidth = FRAME_LINE_WIDTH;
      context.shadowColor = 'rgba(0,0,0,0.5)';
      context.shadowOffsetX = 3;
      context.shadowOffsetY = 3;
      context.shadowBlur = 8;
      context.beginPath();
      context.rect(LEFT_MARGIN, topFrame, this.width, HEIGHT);
      context.moveTo(LEFT_MARGIN + FRAME_LINE_WIDTH / 2, topFrame + HEAVEN);
      context.lineTo(LEFT_MARGIN + this.width - FRAME_LINE_WIDTH / 2, topFrame + HEAVEN);
      context.stroke();
      
      const middle = Math.floor(this.numberOfRods / 2);
      context.lineWidth = 1;
      context.strokeStyle = DOT_STROKE_STYLE;
      context.fillStyle = DOT_FILL_STYLE;
      for (let i = 0, x = LEFT_MARGIN + DISTANCE_RODS; i < this.numberOfRods; ++i, x += DISTANCE_RODS) {
        if ((i - middle) % 3 === 0) {
          context.beginPath();
          context.arc(x, topFrame + HEAVEN, DOT_SIZE, 0, Math.PI * 2, false);
          context.fill();
          context.stroke();
        }
      }
      context.restore();
    }

    drawRods(context, topFrame, showNumbers) {
      context.save();
      context.strokeStyle = ROD_STROKE_STYLE;
      context.lineWidth = ROD_LINE_WIDTH;
      for (let i = 0; i < this.numberOfRods; i++) {
        const rod = this.rods[i];
        rod.draw(context, topFrame, showNumbers);
      }
      context.restore();
    }

    draw(context, canvas, showNumbers) {
      context.save();
      const topFrame = showNumbers ? TOP_MARGIN + NUMBER_HEIGHT : FRAME_PAD_COMPACT;
      const bottomPad = showNumbers ? 10 : FRAME_PAD_COMPACT;
      canvas.height = topFrame + HEIGHT + bottomPad;
      context.clearRect(0, 0, canvas.width, canvas.height);
      this.drawRods(context, topFrame, showNumbers);
      this.drawFrame(context, topFrame);
      context.restore();
    }

    reset() {
      for (let i = 0; i < this.numberOfRods; i++) {
        const rod = this.rods[i];
        rod.reset();
      }
      this.showNumbers = true;
    }

    getValue() {
      let total = 0;
      for (let i = 0; i < this.numberOfRods; i++) {
        total += this.rods[i].value * Math.pow(10, this.numberOfRods - i - 1);
      }
      return total;
    }
  }

  class RodClass {
    constructor(position, beads, value) {
      this.position = position;
      this.beads = beads;
      this.value = 0;
      this.disabled = false;
      this.invisible = false;
    }

    drawBeads(context, topFrame) {
      for (let i = 0; i < this.beads.length; i++) {
        this.beads[i].draw(context, topFrame);
      }
    }

    drawRod(context, topFrame) {
      context.save();
      context.strokeStyle = ROD_STROKE_STYLE;
      context.lineWidth = ROD_LINE_WIDTH;
      if (this.invisible) {
        context.globalAlpha = 0;
      } else if (this.disabled) {
        context.globalAlpha = 0.1;
      } else {
        context.globalAlpha = 1;
      }
      context.shadowColor = 'rgba(0,0,0,0.5)';
      context.shadowOffsetX = 3;
      context.shadowOffsetY = 3;
      context.shadowBlur = 8;
      context.beginPath();
      context.moveTo(this.evalXPos(), topFrame);
      context.lineTo(this.evalXPos(), topFrame + HEIGHT);
      context.stroke();
      context.restore();
    }

    draw(context, topFrame, showNumbers) {
      this.drawRod(context, topFrame);
      this.drawBeads(context, topFrame);
      if (showNumbers) {
        this.writeValue(context, topFrame);
      }
    }

    evalXPos() {
      return LEFT_MARGIN + this.position * DISTANCE_RODS;
    }

    reset() {
      for (let i = 0; i < this.beads.length; i++) {
        this.beads[i].reset();
      }
      this.value = 0;
    }

    writeValue(context, topFrame) {
      if (this.invisible) {
        context.globalAlpha = 0;
      } else if (this.disabled) {
        context.globalAlpha = 0.1;
      } else {
        context.globalAlpha = 1;
      }
      context.font = "bold 40px Courier New, Courier, monospace";
      context.textAlign = "center";
      context.lineWidth = 1;
      context.shadowColor = 'rgba(0,0,0,0.2)';
      context.shadowOffsetX = 3;
      context.shadowOffsetY = 3;
      context.shadowBlur = 8;
      context.fillStyle = '#228B22';
      context.strokeStyle = '#228B22';
      context.fillText(this.value, this.evalXPos(), TOP_MARGIN);
      context.strokeText(this.value, this.evalXPos(), TOP_MARGIN);
    }
  }

  class BeadClass {
    constructor(rod, heaven, order, active) {
      this.rod = rod;
      this.heaven = heaven;
      this.order = order;
      this.active = active;
    }

    getPoints(topFrame) {
      const points = [];
      const center = this.evalPosition(topFrame);
      points.push({ x: center.x - BEAD_WIDTH / 2, y: center.y });
      points.push({ x: center.x + BEAD_WIDTH / 2, y: center.y });
      points.push({ x: center.x + BEAD_WIDTH / 6, y: center.y - BEAD_HEIGHT / 2 });
      points.push({ x: center.x - BEAD_WIDTH / 6, y: center.y - BEAD_HEIGHT / 2 });
      points.push({ x: center.x - BEAD_WIDTH / 2, y: center.y });
      points.push({ x: center.x - BEAD_WIDTH / 6, y: center.y + BEAD_HEIGHT / 2 });
      points.push({ x: center.x + BEAD_WIDTH / 6, y: center.y + BEAD_HEIGHT / 2 });
      points.push({ x: center.x + BEAD_WIDTH / 2, y: center.y });
      return points;
    }

    evalPosition(topFrame) {
      const x = LEFT_MARGIN + this.rod.position * DISTANCE_RODS;
      let y;

      if (this.heaven) {
        if (this.active) {
          y = topFrame + HEAVEN - BEAD_HEIGHT / 2 - FRAME_LINE_WIDTH / 2;
        } else {
          y = topFrame + BEAD_HEIGHT / 2 + FRAME_LINE_WIDTH / 2;
        }
      } else {
        if (this.active) {
          y = topFrame + HEAVEN + (this.order - 1) * BEAD_HEIGHT + BEAD_HEIGHT / 2 + FRAME_LINE_WIDTH / 2;
        } else {
          y = topFrame + HEAVEN + this.order * BEAD_HEIGHT + BEAD_HEIGHT / 2 + FRAME_LINE_WIDTH / 2;
        }
      }

      return { x, y };
    }

    createPath(context, topFrame) {
      const points = this.getPoints(topFrame);
      context.beginPath();
      context.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; ++i) {
        context.lineTo(points[i].x, points[i].y);
      }
    }

    draw(context, topFrame) {
      context.save();
      context.shadowColor = 'rgba(0,0,0,0.5)';
      context.shadowOffsetX = 3;
      context.shadowOffsetY = 3;
      context.shadowBlur = 8;
      if (this.active) {
        context.fillStyle = activeColor;
      } else {
        context.fillStyle = beadColor;
      }
      if (this.rod.invisible) {
        context.globalAlpha = 0;
      } else if (this.rod.disabled) {
        context.globalAlpha = 0.1;
      } else {
        context.globalAlpha = 1;
      }
      context.strokeStyle = BEAD_STROKE;
      context.lineWidth = 1;
      this.createPath(context, topFrame);
      context.fill();
      context.stroke();
      context.restore();
    }

    reset() {
      this.active = false;
    }
  }

  // Helper functions للمعداد
  const windowToCanvas = (x, y, canvas) => {
    const bbox = canvas.getBoundingClientRect();
    const sx = canvas.width / bbox.width;
    const sy = canvas.height / bbox.height;
    return {
      x: (x - bbox.left) * sx,
      y: (y - bbox.top) * sy,
    };
  };

  const getPointerClientCoords = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    if (e.changedTouches && e.changedTouches.length > 0) {
      return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const getBead = (rod, heaven, order) => {
    for (let i = 0; i < rod.beads.length; i++) {
      if (rod.beads[i].heaven === heaven && rod.beads[i].order === order) {
        return rod.beads[i];
      }
    }
  };

  const clickedBead = (bead) => {
    if (bead.heaven) {
      if (bead.active) {
        bead.active = false;
        bead.rod.value -= 5;
      } else {
        bead.active = true;
        bead.rod.value += 5;
      }
    } else {
      if (bead.active) {
        bead.active = false;
        bead.rod.value--;
        for (let i = bead.order + 1; i <= 4; i++) {
          const nextBead = getBead(bead.rod, false, i);
          if (nextBead.active) {
            nextBead.active = false;
            nextBead.rod.value--;
          }
        }
      } else {
        bead.active = true;
        bead.rod.value++;
        for (let i = 1; i < bead.order; i++) {
          const nextBead = getBead(bead.rod, false, i);
          if (!nextBead.active) {
            nextBead.active = true;
            nextBead.rod.value++;
          }
        }
      }
    }
  };

  const playBeadSound = () => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  };

  // دالة لقراءة القيمة من المعداد
  const getAbacusValue = () => {
    if (!abacus) return 0;
    return abacus.getValue();
  };

  // صوت التصفيق للإجابة الصحيحة
  const playSuccessSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const clapCount = 6;
      const clapInterval = 0.15;
      
      for (let i = 0; i < clapCount; i++) {
        const oscillator = audioContextRef.current.createOscillator();
        const gainNode = audioContextRef.current.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
        
        oscillator.frequency.setValueAtTime(2000 + Math.random() * 1000, audioContextRef.current.currentTime + i * clapInterval);
        oscillator.type = 'square';
        
        gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime + i * clapInterval);
        gainNode.gain.linearRampToValueAtTime(0.3, audioContextRef.current.currentTime + i * clapInterval + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + i * clapInterval + 0.1);
        
        oscillator.start(audioContextRef.current.currentTime + i * clapInterval);
        oscillator.stop(audioContextRef.current.currentTime + i * clapInterval + 0.1);
      }
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // صوت "Ohhhhh" للإجابة الخاطئة
  const playEncouragementSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.setValueAtTime(150, audioContextRef.current.currentTime);
      oscillator.frequency.setValueAtTime(120, audioContextRef.current.currentTime + 0.3);
      oscillator.frequency.setValueAtTime(100, audioContextRef.current.currentTime + 0.6);
      oscillator.type = 'sawtooth';
      
      gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, audioContextRef.current.currentTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0.15, audioContextRef.current.currentTime + 0.4);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 1.2);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 1.2);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // معالجة تغيير الإعدادات
  const handleSettingChange = (key, value) => {
    setSessionSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getFlashCardType = () => {
    // Display Type order in UI:
    // 1) multipleChoice, 2) text, 3) soroban
    switch (sessionSettings.displayType) {
      case 'multipleChoice':
        return 1;
      case 'text':
        return 2;
      case 'soroban':
        return 3;
      default:
        return 1;
    }
  };

  // إنشاء الجلسة الأولى
  const createSession = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/education/flash-cards/sessions', {
        totalCards: parseInt(sessionSettings.totalCards),
        digitsPerNumber: parseInt(sessionSettings.digitsPerNumber),
        estimatedTimePerCard: parseInt(sessionSettings.estimatedTimePerCard),
        flashCardType: getFlashCardType()
      });

      if (response.data.success) {
        setSessionData(response.data.data);
        setCurrentRound(1);
        setRoundResults([]);
        setAllSessionsData([response.data.data]);
        setGameState('ready');
        toast.success(response.data.message);
        // تحديث الأرشيف بعد إنشاء جلسة جديدة
        fetchArchiveData();
      }
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error(error.response?.data?.message || 'Failed to create session');
    } finally {
      setIsLoading(false);
    }
  };

  // إنشاء جلسة جديدة للجولة التالية وبدءها مباشرة
  const createNextSession = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/education/flash-cards/sessions', {
        totalCards: parseInt(sessionSettings.totalCards),
        digitsPerNumber: parseInt(sessionSettings.digitsPerNumber),
        estimatedTimePerCard: parseInt(sessionSettings.estimatedTimePerCard),
        flashCardType: getFlashCardType()
      });

      if (response.data.success) {
        const newSessionData = response.data.data;
        setSessionData(newSessionData);
        setAllSessionsData(prev => [...prev, newSessionData]);
        
        // بدء الجلسة مباشرة
        try {
          const startResponse = await api.post(`/education/flash-cards/sessions/${newSessionData.sessionId}/start`);
          
          if (startResponse.data.success) {
            setSessionData(startResponse.data.data);
            setGameState('playing');
            setSessionStartTime(Date.now());
            setCardStartTime(Date.now());
            setCurrentCardIndex(0);
            setAnswers([]);
            toast.success(`${t('flashCards.round')} ${currentRound + 1} ${t('flashCards.sessionReady')}!`);
          }
        } catch (startError) {
          console.error('Error starting session:', startError);
          toast.error(startError.response?.data?.message || 'Failed to start session');
          setGameState('ready');
        }
      }
    } catch (error) {
      console.error('Error creating next session:', error);
      toast.error(error.response?.data?.message || 'Failed to create next session');
    } finally {
      setIsLoading(false);
    }
  };

  // بدء الجلسة
  const startSession = async () => {
    if (!sessionData) return;

    setIsLoading(true);
    try {
      const response = await api.post(`/education/flash-cards/sessions/${sessionData.sessionId}/start`);
      
      if (response.data.success) {
        setSessionData(response.data.data);
        setGameState('playing');
        setSessionStartTime(Date.now());
        setCardStartTime(Date.now());
        setCurrentCardIndex(0);
        setAnswers([]);
      }
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error(error.response?.data?.message || 'Failed to start session');
    } finally {
      setIsLoading(false);
    }
  };

  // معالجة اختيار الإجابة
  const handleAnswerSelection = (selectedAnswer) => {
    const currentCard = sessionData?.cards?.[currentCardIndex];
    if (!currentCard) return;

    let answerValue = null;
    
    // تحديد قيمة الإجابة حسب نوع العرض
    if (sessionSettings.displayType === 'multipleChoice') {
      answerValue = selectedAnswer;
    } else if (sessionSettings.displayType === 'text') {
      if (!studentAnswer) return;
      answerValue = parseInt(studentAnswer);
    } else if (sessionSettings.displayType === 'soroban') {
      answerValue = abacusValue ?? getAbacusValue();
    }

    if (answerValue === null) return;

    const timeMs = Date.now() - cardStartTime;
    const isCorrect = answerValue === currentCard.correctAnswer;

    // حفظ الإجابة محلياً
    const answerData = {
      cardId: currentCard.cardId,
      studentAnswer: answerValue,
      isCorrect: isCorrect,
      timeMs: timeMs
    };

    setAnswers(prev => [...prev, answerData]);
    
    // إعادة تعيين الحالة
    setStudentAnswer('');
    setAbacusValue(0);
    // إعادة تعيين المعداد
    if (abacus) {
      abacus.reset();
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        abacus.draw(context, canvas, showNumbers);
      }
    }

    // الانتقال للبطاقة التالية أو إنهاء الجلسة
    if (currentCardIndex < sessionData.cards.length - 1) {
      setCurrentCardIndex(prev => prev + 1);
      setCardStartTime(Date.now());
      setAbacusValue(0);
      // إعادة تعيين المعداد للبطاقة التالية
      if (abacus && sessionSettings.displayType === 'soroban') {
        abacus.reset();
        if (canvasRef.current) {
          const canvas = canvasRef.current;
          const context = canvas.getContext('2d');
          abacus.draw(context, canvas, showNumbers);
        }
      }
    } else {
      // انتهاء جميع البطاقات - إرسال النتائج
      submitAllAnswers([...answers, answerData]);
    }
  };

  // إرسال جميع الإجابات
  const submitAllAnswers = async (allAnswers) => {
    if (!sessionData) return;

    setIsLoading(true);
    const totalTimeMs = Date.now() - sessionStartTime;
    const correctAnswers = allAnswers.filter(a => a.isCorrect).length;

    try {
      const response = await api.post(`/education/flash-cards/sessions/${sessionData.sessionId}/submit`, {
        correctAnswers: correctAnswers,
        totalTimeMs: totalTimeMs,
        answers: allAnswers
      });

      if (response.data.success) {
        const roundResult = response.data.data;
        setResult(roundResult);
        
        // حفظ نتيجة الجولة الحالية
        setRoundResults(prev => [...prev, roundResult]);
        
        // التحقق من انتهاء جميع الجولات
        if (currentRound >= parseInt(sessionSettings.totalRounds)) {
          setGameState('result');
          toast.success(t('competitions.allRoundsCompleted'));
        } else {
          setGameState('roundComplete');
          toast.success(`${t('flashCards.round')} ${currentRound} ${t('common.finish')}!`);
          // تشغيل صوت التصفيق في نهاية الجولة
          playSuccessSound();
        }
        
        // تحديث الأرشيف
        fetchArchiveData();
        fetchOverallStatistics();
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
      toast.error(error.response?.data?.message || 'Failed to submit answers');
    } finally {
      setIsLoading(false);
    }
  };

  // الانتقال للجولة التالية
  const nextRound = () => {
    if (currentRound < parseInt(sessionSettings.totalRounds)) {
      setCurrentRound(prev => prev + 1);
      setCurrentCardIndex(0);
      setAnswers([]);
      setResult(null);
      setSessionStartTime(null);
      setCardStartTime(null);
      createNextSession();
    }
  };

  // إعادة التشغيل
  const resetGame = () => {
    setGameState('settings');
    setSessionData(null);
    setCurrentCardIndex(0);
    setAnswers([]);
    setStudentAnswer('');
    setAbacusValue(0);
    setResult(null);
    // إعادة تعيين المعداد
    if (abacus) {
      abacus.reset();
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        abacus.draw(context, canvas, showNumbers);
      }
    }
    setSessionStartTime(null);
    setCardStartTime(null);
    setCurrentRound(1);
    setRoundResults([]);
    setAllSessionsData([]);
  };

  // تنظيف عند إلغاء التحميل
  useEffect(() => {
    return () => {
      const ctx = audioContextRef.current;
      if (ctx && ctx.state !== 'closed') {
        void ctx.close();
      }
      audioContextRef.current = null;
    };
  }, []);

  // تشغيل الصوت المناسب عند ظهور النتيجة
  useEffect(() => {
    if (gameState === 'result' && result) {
      const accuracyPercentage = result.accuracyPercentage || 0;
      if (accuracyPercentage >= 60) {
        playSuccessSound();
      } else {
        playEncouragementSound();
      }
    }
  }, [gameState, result]);

  // جلب بيانات الأرشيف - فقط الجلسات المكتملة
  const fetchArchiveData = async (page = 1) => {
    setLoadingArchive(true);
    try {
      const response = await api.get(`/education/flash-cards/sessions?page=${page}&limit=10&status=completed`);
      if (response.data.success) {
        // تصفية البيانات للتأكد من عرض فقط الجلسات المكتملة
        const completedSessions = response.data.data.filter(session => session.status === 'completed');
        setArchiveData(completedSessions);
        // تحديث pagination إذا كانت البيانات مصفاة
        if (completedSessions.length !== response.data.data.length) {
          setArchivePagination({
            ...response.data.pagination,
            totalCount: completedSessions.length
          });
        } else {
          setArchivePagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Error fetching archive data:', error);
      toast.error('Failed to load archive data');
    } finally {
      setLoadingArchive(false);
    }
  };

  // جلب الإحصائيات العامة
  const fetchOverallStatistics = async () => {
    setLoadingStats(true);
    try {
      const response = await api.get('/education/flash-cards/statistics');
      if (response.data.success) {
        const data = response.data.data;
        setOverallStats({
          totalSessions: data.totalSessions || 0,
          totalCorrectAnswers: data.totalCorrectAnswers || 0,
          totalCardsAttempted: data.totalCardsAttempted || 0,
          avgScore: parseFloat(data.averageScore) || 0,
          avgAccuracy: parseFloat(data.averageAccuracy) || 0,
          avgTime: parseFloat(data.averageTimeMs) || 0,
          totalScore: parseFloat(data.totalScore) || 0
        });
      }
    } catch (error) {
      console.error('Error fetching overall statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // جلب البيانات عند تحميل المكون
  useEffect(() => {
    fetchArchiveData();
    fetchOverallStatistics();
  }, []);

  // تهيئة المعداد عند تغيير البطاقة أو بدء الجلسة
  useEffect(() => {
    if (gameState === 'playing' && sessionSettings.displayType === 'soroban' && canvasRef.current && sessionData) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      const numberOfRods = parseInt(sessionData?.digitsPerNumber) || 9;
      
      // في الخيار الثالث لا نعرض الأرقام العلوية
      const newAbacus = new AbacusClass(numberOfRods, 'normal', frameColor, false);
      canvas.width = newAbacus.width + 2 * LEFT_MARGIN;
      
      setAbacus(newAbacus);
      newAbacus.draw(context, canvas, false);
      setAbacusValue(0); // إعادة تعيين القيمة عند تهيئة المعداد
      
      const handlePointer = (e) => {
        if (!newAbacus || !canvas) return;
        if (e.type === 'touchstart') {
          e.preventDefault();
        }
        const { x: cx, y: cy } = getPointerClientCoords(e);
        const loc = windowToCanvas(cx, cy, canvas);
        let found = false;

        const topFrameHit = FRAME_PAD_COMPACT;
        for (let i = 0; i < newAbacus.numberOfRods && !found; i++) {
          const currentRod = newAbacus.rods[i];
          for (let j = 0; j < currentRod.beads.length && !found; j++) {
            const currentBead = currentRod.beads[j];
            currentBead.createPath(context, topFrameHit);
            if (context.isPointInPath(loc.x, loc.y)) {
              found = true;
              playBeadSound();
              clickedBead(currentBead);
            }
          }
        }

        if (found) {
          context.clearRect(0, 0, canvas.width, canvas.height);
          newAbacus.draw(context, canvas, false);
          const newValue = newAbacus.getValue();
          setAbacusValue(newValue);
        }
      };

      canvas.addEventListener('click', handlePointer);
      canvas.addEventListener('touchstart', handlePointer, { passive: false });

      return () => {
        canvas.removeEventListener('click', handlePointer);
        canvas.removeEventListener('touchstart', handlePointer);
      };
    }
  }, [gameState, currentCardIndex, sessionData, sessionSettings.displayType, showNumbers]);


  // عرض شاشة الإعدادات
  if (gameState === 'settings') {
  return (

      <div className="mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10" dir={isRTL ? 'rtl' : 'ltr'}>
        <DailyExercisesHero
          title={t('navigation.flashCards')}
          description={t('flashCards.pageDescription')}
          icon={CreditCardIcon}
          iconClassName="text-emerald-600"
        />

      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/90 shadow-[0_8px_0_0_rgba(15,23,42,0.06)] backdrop-blur-sm">
        <div className="border-b border-slate-200/90 bg-gradient-to-r from-emerald-50/90 via-white to-amber-50/50 px-4 py-3 sm:px-5">
          <h3 className="text-base font-black text-slate-900 sm:text-lg">
            {t('flashCards.sessionSettings')}
          </h3>
        </div>
        <div className="p-4 sm:p-6">
               <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-5 lg:gap-y-4">
                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashCards.totalCards')}
                   </label>
                   <input
                     type="number"
                     min="1"
                     max="20"
                     className={kidInputClass}
                     value={sessionSettings.totalCards}
                     onChange={(e) => handleSettingChange('totalCards', e.target.value)}
                     placeholder="5"
                   />
                 </div>

                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashCards.digitsPerNumber')}
                   </label>
                   <input
                     type="number"
                     min="1"
                     max="9"
                     className={kidInputClass}
                     value={sessionSettings.digitsPerNumber}
                     onChange={(e) => handleSettingChange('digitsPerNumber', e.target.value)}
                     placeholder="1"
                   />
                 </div>

                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashCards.estimatedTimePerCard')}
                   </label>
                   <input
                     type="number"
                     min="1000"
                     max="10000"
                     step="500"
                     className={kidInputClass}
                     value={sessionSettings.estimatedTimePerCard}
                     onChange={(e) => handleSettingChange('estimatedTimePerCard', e.target.value)}
                     placeholder="3000"
                   />
                 </div>

                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashCards.totalRounds')}
                   </label>
                   <input
                     type="number"
                     min="1"
                     max="10"
                     className={kidInputClass}
                     value={sessionSettings.totalRounds}
                     onChange={(e) => handleSettingChange('totalRounds', e.target.value)}
                     placeholder="3"
                   />
                 </div>

                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashCards.displayType')}
                   </label>
                   {flashCardsSorobanOnlyByLevel ? (
                     <>
                       <div
                         className={`${kidInputClass} flex max-w-full cursor-not-allowed items-center bg-slate-100 py-0.5 text-slate-700`}
                         aria-readonly
                       >
                         {t('flashCards.displayTypeSoroban')}
                       </div>
                       <p className="mt-1.5 text-[11px] font-semibold leading-snug text-slate-500 sm:text-xs">
                         {t('flashCards.displayTypeSorobanOnlyByLevelHint')}
                       </p>
                     </>
                   ) : (
                     <select
                       className={`${kidInputClass} max-w-full`}
                       value={sessionSettings.displayType}
                       onChange={(e) => handleSettingChange('displayType', e.target.value)}
                     >
                       <option value="multipleChoice">{t('flashCards.displayTypeMultipleChoice')}</option>
                       <option value="text">{t('flashCards.displayTypeText')}</option>
                       <option value="soroban">{t('flashCards.displayTypeSoroban')}</option>
                     </select>
                   )}
                 </div>
               </div>

               <div className="flex justify-center">
                 <button
                   type="button"
                   onClick={createSession}
                   disabled={isLoading}
                   className="inline-flex min-h-[3.25rem] items-center gap-3 rounded-2xl border-[3px] border-sky-800/25 bg-gradient-to-b from-sky-400 to-sky-600 px-10 py-4 text-lg font-black text-white shadow-[0_4px_0_0_rgba(15,23,42,0.25)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                 >
                   <PlayIcon className="h-7 w-7 shrink-0" strokeWidth={2.5} />
                   {isLoading ? t('flashCards.creating') : t('flashCards.startSession')}
                 </button>
               </div>
             </div>
      </section>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-4">
          <div className="min-w-0 rounded-[1.35rem] border-[3px] border-sky-200/80 bg-gradient-to-br from-sky-50/95 to-white p-4 shadow-[0_8px_0_0_rgba(14,165,233,0.1)] sm:p-5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-sky-200 bg-white">
                <svg className="h-6 w-6 text-sky-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div className="min-w-0 flex-1 text-end">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('flashCards.totalSessions')}</p>
                <p className="text-2xl font-black tabular-nums text-slate-900 sm:text-3xl">{loadingStats ? '…' : overallStats.totalSessions}</p>
              </div>
            </div>
            <div className="rounded-xl border border-sky-100 bg-sky-50/80 px-3 py-2">
              <p className="break-words text-xs font-semibold leading-snug text-sky-900/90">{t('flashCards.totalSessionsDescription')}</p>
            </div>
          </div>

          <div className="min-w-0 rounded-[1.35rem] border-[3px] border-emerald-200/80 bg-gradient-to-br from-emerald-50/95 to-white p-4 shadow-[0_8px_0_0_rgba(16,185,129,0.1)] sm:p-5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-emerald-200 bg-white">
                <svg className="h-6 w-6 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1 text-end">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('flashCards.accuracy')}</p>
                <p className="text-2xl font-black tabular-nums text-slate-900 sm:text-3xl">{loadingStats ? '…' : `${overallStats.avgAccuracy.toFixed(1)}%`}</p>
              </div>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 px-3 py-2">
              <p className="break-words text-xs font-semibold leading-snug text-emerald-900/90">
                {overallStats.totalCorrectAnswers} {t('flashCards.outOf')} {overallStats.totalCardsAttempted} {t('flashCards.cardsCorrect')}
              </p>
            </div>
          </div>

          <div className="min-w-0 rounded-[1.35rem] border-[3px] border-amber-200/80 bg-gradient-to-br from-amber-50/95 to-white p-4 shadow-[0_8px_0_0_rgba(245,158,11,0.12)] sm:p-5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-amber-200 bg-white">
                <svg className="h-6 w-6 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1 text-end">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('flashCards.avgScore')}</p>
                <p className="text-2xl font-black tabular-nums text-slate-900 sm:text-3xl">{loadingStats ? '…' : overallStats.avgScore.toFixed(1)}</p>
              </div>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-3 py-2">
              <p className="break-words text-xs font-semibold leading-snug text-amber-950/90">{t('flashCards.averageScoreDescription')}</p>
            </div>
          </div>

          <div className="min-w-0 rounded-[1.35rem] border-[3px] border-violet-200/80 bg-gradient-to-br from-violet-50/95 to-white p-4 shadow-[0_8px_0_0_rgba(139,92,246,0.1)] sm:p-5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-violet-200 bg-white">
                <svg className="h-6 w-6 text-violet-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="min-w-0 flex-1 text-end">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('flashCards.totalScore')}</p>
                <p className="text-2xl font-black tabular-nums text-slate-900 sm:text-3xl">{loadingStats ? '…' : overallStats.totalScore.toFixed(1)}</p>
              </div>
            </div>
            <div className="rounded-xl border border-violet-100 bg-violet-50/80 px-3 py-2">
              <p className="break-words text-xs font-semibold leading-snug text-violet-950/90">{t('flashCards.totalScoreDescription')}</p>
            </div>
          </div>
        </div>

      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/90 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
          <div className="border-b border-slate-200/90 bg-gradient-to-r from-sky-50/90 via-white to-amber-50/50 px-4 py-4 sm:px-6">
            <h3 className="text-base font-black text-slate-900 sm:text-lg">
              {t('flashCards.archive')}
            </h3>
            <p className="mt-1 text-xs font-semibold text-slate-600 sm:text-sm">
              {t('flashCards.archiveDescription')}
        </p>
      </div>


          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/90">
                <tr>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashCards.sessionId')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashCards.status')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashCards.totalCards')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashCards.correct')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashCards.completedAt')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashCards.duration')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashCards.score')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loadingArchive ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-6 text-center text-sm font-semibold text-slate-500 sm:px-6">
                      {t('flashCards.loading')}
                    </td>
                  </tr>
                ) : archiveData.length > 0 ? (
                  archiveData.map((session, index) => {
                    const accuracyPercentage = session.totalCards > 0 
                      ? ((session.correctAnswers / session.totalCards) * 100).toFixed(0)
                      : 0;
                    const isCorrect = accuracyPercentage >= 60;
                    
                    // تنسيق تاريخ الإنهاء
                    const formatDate = (dateString) => {
                      if (!dateString) return 'N/A';
                      const date = new Date(dateString);
                      return date.toLocaleString(isRTL ? 'ar-SA' : 'fr-FR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                    };

                    // تنسيق المدة
                    const formatDuration = (ms) => {
                      if (!ms) return 'N/A';
                      const seconds = Math.floor(ms / 1000);
                      const minutes = Math.floor(seconds / 60);
                      const remainingSeconds = seconds % 60;
                      
                      if (minutes > 0) {
                        return `${minutes}m ${remainingSeconds}s`;
                      }
                      return `${seconds}s`;
                    };
                    
                    return (
                      <tr key={index} className="transition-colors hover:bg-sky-50/60">
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-900 sm:px-6">
                          {session.sessionId}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 sm:px-6">
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${
                            session.status === 'completed' 
                              ? 'bg-green-100 text-green-800' 
                              : session.status === 'active'
                              ? 'bg-yellow-100 text-yellow-800'
                              : session.status === 'created'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {session.status === 'completed' ? t('flashCards.statusCompleted') :
                             session.status === 'active' ? t('flashCards.statusInProgress') :
                             session.status === 'created' ? t('flashCards.statusCreated') :
                             t('flashCards.statusCancelled')}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-800 sm:px-6">
                          {session.totalCards}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-start sm:px-6">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                            isCorrect 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {isCorrect ? (
                              <>
                                <CheckCircleIcon className="me-1 h-3 w-3 shrink-0" />
                                {session.correctAnswers || 0}/{session.totalCards}
                              </>
                            ) : (
                              <>
                                <XCircleIcon className="me-1 h-3 w-3 shrink-0" />
                                {session.correctAnswers || 0}/{session.totalCards}
                              </>
                            )}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-800 sm:px-6">
                          {formatDate(session.completedAt)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-slate-700 sm:px-6">
                          {formatDuration(session.totalTimeMs)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm sm:px-6">
                          <span className="font-black text-sky-600 tabular-nums">{session.finalScore || 0}</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-sm font-semibold text-slate-500 sm:px-6">
                      {t('flashCards.noSessionsYet')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {archivePagination.totalPages > 1 && (
            <div className="flex flex-col gap-3 border-t border-slate-200/90 bg-slate-50/50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="flex flex-1 justify-between gap-2 sm:hidden">
                <button
                  type="button"
                  onClick={() => fetchArchiveData(archivePagination.currentPage - 1)}
                  disabled={!archivePagination.hasPrevPage}
                  className="inline-flex min-h-[2.5rem] flex-1 items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('flashCards.previous')}
                </button>
                <button
                  type="button"
                  onClick={() => fetchArchiveData(archivePagination.currentPage + 1)}
                  disabled={!archivePagination.hasNextPage}
                  className="inline-flex min-h-[2.5rem] flex-1 items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('flashCards.next')}
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-600">
                    {t('flashCards.showing')} <span className="font-medium">{((archivePagination.currentPage - 1) * archivePagination.limit) + 1}</span> {t('flashCards.to')} <span className="font-medium">{Math.min(archivePagination.currentPage * archivePagination.limit, archivePagination.totalCount)}</span> {t('flashCards.of')} <span className="font-medium">{archivePagination.totalCount}</span> {t('flashCards.results')}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex overflow-hidden rounded-xl border-2 border-slate-200 bg-white shadow-sm" aria-label="Pagination">
                    <button
                      type="button"
                      onClick={() => fetchArchiveData(archivePagination.currentPage - 1)}
                      disabled={!archivePagination.hasPrevPage}
                      className="relative inline-flex items-center px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t('flashCards.previous')}
                    </button>
                    
                    {Array.from({ length: archivePagination.totalPages }, (_, i) => i + 1).map((page) => {
                      const isNearCurrentPage = Math.abs(page - archivePagination.currentPage) <= 2;
                      const isFirstPage = page === 1;
                      const isLastPage = page === archivePagination.totalPages;
                      
                      if (isNearCurrentPage || isFirstPage || isLastPage) {
                        return (
                          <button
                            type="button"
                            key={page}
                            onClick={() => fetchArchiveData(page)}
                            className={`relative inline-flex min-w-[2.5rem] items-center justify-center border-s border-slate-200 px-3 py-2 text-sm font-bold ${
                              page === archivePagination.currentPage
                                ? 'z-10 bg-gradient-to-b from-sky-400 to-sky-600 text-white'
                                : 'text-slate-600 hover:bg-sky-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === archivePagination.currentPage - 3 || page === archivePagination.currentPage + 3) {
                        return (
                          <span key={`dots-${page}`} className="inline-flex items-center border-s border-slate-200 px-2 py-2 text-sm font-bold text-slate-400">
                            …
                          </span>
                        );
                      }
                      return null;
                    })}
                    
                    <button
                      type="button"
                      onClick={() => fetchArchiveData(archivePagination.currentPage + 1)}
                      disabled={!archivePagination.hasNextPage}
                      className="relative inline-flex items-center border-s border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t('flashCards.next')}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
      </section>
       </div>
    );
  }

  // شاشة جاهز للبدء - في مودال
  if (gameState === 'ready') {
    return (
         <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-6 sm:overflow-hidden">
          <div className="text-center">
            <div className="mb-6 sm:mb-8">
              <CreditCardIcon className="mx-auto mb-3 h-20 w-20 text-yellow-400 sm:mb-4 sm:h-24 sm:w-24" />
              <h1 className="text-4xl font-bold text-white mb-4">
                {t('navigation.flashCards')}
              </h1>
              <p className="text-xl text-gray-300 mb-2">
                {t('flashCards.sessionReady')}
              </p>
              <div className="text-gray-400 space-y-1">
                <p>{sessionData.totalCards} {t('flashCards.cards')}</p>
                <p>{sessionData.digitsPerNumber} {t('flashCards.digitsPerNumber')}</p>
              </div>
            </div>
            
            <button
              onClick={startSession}
              disabled={isLoading}
              className="px-12 py-4 bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl rounded-lg transition-colors disabled:opacity-50"
            >
              {isLoading ? t('flashCards.starting') : t('flashCards.startNow')}
            </button>
          </div>
        </div>
    );
  }

   // شاشة اللعب - عرض البطاقة الحالية مع الخيارات
   if (gameState === 'playing') {
     const currentCard = sessionData?.cards?.[currentCardIndex];
     const progress = ((currentCardIndex + 1) / sessionData?.cards?.length) * 100;

     if (!currentCard) return null;

     return (
         <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-black">
           <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden px-2 py-2 sm:px-4 sm:py-3">
           <FlashTickOverlay tickKey={currentCardIndex} className="rounded-none" />
           <div
             className={`relative z-[1] mx-auto flex min-h-0 w-full flex-1 flex-col overflow-hidden px-1 text-center sm:px-3 ${
               sessionSettings.displayType === 'soroban' ? 'max-w-4xl' : 'max-w-2xl'
             }`}
           >

             {/* رقم البطاقة */}
             <div className="mb-1 shrink-0 text-sm text-gray-400 sm:mb-2 sm:text-lg">
               {t('flashCards.card')} {currentCardIndex + 1} / {sessionData?.cards?.length}
             </div>
             
             {/* عرض السوروبان - فقط للخيارات الأخرى وليس soroban */}
             {sessionSettings.displayType !== 'soroban' && (
               <div className="mb-3 flex min-h-0 flex-1 items-center justify-center sm:mb-4">
                 <div className="max-h-[min(52dvh,420px)] overflow-hidden rounded-xl bg-white p-3 shadow-2xl sm:p-6">
                   <SorobanDisplay 
                     number={currentCard.displayedNumber} 
                     digitsPerNumber={sessionData.digitsPerNumber}
                     size="xlarge"
                   />
                 </div>
               </div>
             )}

             {/* سؤال - فقط للخيارات الأخرى وليس soroban */}
             {sessionSettings.displayType !== 'soroban' && (
               <h2 className="mb-2 shrink-0 text-lg font-bold text-white sm:text-xl">
                 {t('flashCards.whatIsTheNumber')}
               </h2>
             )}
             
             {/* نوع العرض: اختيار متعدد */}
             {sessionSettings.displayType === 'multipleChoice' && (
               <div className="grid min-h-0 shrink-0 grid-cols-2 gap-2 sm:gap-3">
                 {currentCard.options.map((option, index) => (
                   <button
                     key={index}
                     onClick={() => handleAnswerSelection(option)}
                     disabled={isLoading}
                     className="px-3 py-3 sm:px-6 sm:py-4 text-[clamp(1rem,4.5vw,1.5rem)] sm:text-2xl font-bold bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed border-2 border-gray-600 hover:border-yellow-500 break-all sm:break-normal"
                   >
                     {option}
                   </button>
                 ))}
               </div>
             )}

             {/* نوع العرض: نصي */}
             {sessionSettings.displayType === 'text' && (
               <div className="mt-2 flex min-h-0 flex-1 flex-col justify-center gap-2">
                 <input
                   type="text"
                   inputMode="numeric"
                   pattern="[0-9]*"
                   value={studentAnswer}
                   onChange={(e) => {
                     const value = e.target.value;
                     if (value === '' || /^\d*$/.test(value)) {
                       setStudentAnswer(value);
                     }
                   }}
                   placeholder={t('flashCards.yourAnswer')}
                   className="w-full px-6 py-4 text-2xl text-center bg-gray-800 text-white border-2 border-gray-600 rounded-lg focus:border-yellow-500 focus:outline-none mb-4"
                   autoFocus
                   onKeyPress={(e) => {
                     if (e.key === 'Enter' && studentAnswer) {
                       handleAnswerSelection(null);
                     }
                   }}
                 />
                 <button
                   onClick={() => handleAnswerSelection(null)}
                   disabled={isLoading || !studentAnswer}
                   className="w-full px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold text-xl rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   {t('flashCards.submitAnswer')}
                 </button>
               </div>
             )}

             {/* نوع العرض: سوروبان */}
             {sessionSettings.displayType === 'soroban' && (
               <div className="flex min-h-0 w-full flex-1 flex-col gap-2 overflow-hidden sm:gap-3">
                 <div className="flex min-h-0 min-w-0 flex-[1.55] flex-col items-center justify-center overflow-hidden px-0.5 py-1 sm:px-1 sm:py-2">
                   {/* بدون flex-1 على الإطار الأبيض حتى لا يمتد عمودياً وتختفي هوامش الـ padding على الشاشات العريضة */}
                   <div className="box-border w-full max-w-full shrink-0 overflow-hidden rounded-xl border border-white/10 bg-white p-2 sm:p-3 min-[580px]:p-4">
                     <canvas
                       ref={canvasRef}
                       className="mx-auto block h-auto max-h-[min(48dvh,440px)] w-full max-w-full object-contain object-center sm:max-h-[min(52dvh,480px)] min-[580px]:max-h-[min(50dvh,520px)]"
                       style={{ cursor: 'pointer', touchAction: 'none' }}
                     />
                   </div>
                   <audio ref={audioRef} preload="auto">
                     <source src="https://www.rightlobemath.com/digitalabacus/clac.wav" type="audio/wav" />
                   </audio>
                 </div>
                 <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center overflow-hidden">
                   <p className="mb-1 shrink-0 text-center text-sm font-semibold text-white sm:mb-2 sm:text-base">
                     {t('flashCards.enterNumberOnSoroban')}
                   </p>
                   <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden py-1">
                     <FullscreenNumberDisplay
                       value={currentCard.correctAnswer}
                       tickKey={currentCardIndex}
                       theme="dark"
                       frameless
                       className="min-h-0 w-full flex-1"
                     />
                   </div>
                 </div>
                 <button
                   type="button"
                   onClick={() => handleAnswerSelection(null)}
                   disabled={isLoading}
                   className="w-full shrink-0 rounded-lg bg-green-500 px-4 py-2.5 text-base font-bold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50 sm:py-3 sm:text-lg"
                 >
                   {t('flashCards.submitAnswer')}
                 </button>
               </div>
             )}
           </div>
           </div>
         </div>
     );
   }

   // شاشة انتهاء الجولة
   if (gameState === 'roundComplete' && result) {
     const accuracyPercentage = result.accuracyPercentage || 0;
     const isGoodResult = accuracyPercentage >= 60;

     return (
         <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-3 py-4">
           <div className="w-full max-w-2xl px-4 text-center">
             <div className="mb-6 sm:mb-8">
               <div className="mb-3 text-6xl sm:mb-4 sm:text-8xl">
                 {isGoodResult ? '🎉' : '💪'}
               </div>
               
               <h2 className={`mb-3 text-2xl font-bold sm:mb-4 sm:text-4xl ${isGoodResult ? 'text-green-400' : 'text-yellow-400'}`}>
                 {isGoodResult ? t('flashCards.excellent') : t('flashCards.keepTrying')}
               </h2>
               
               <div className="mb-2 text-lg text-white sm:text-2xl">
                 {t('flashCards.round')} {currentRound} {t('flashCards.roundOf')} {sessionSettings.totalRounds}
               </div>
               
               <div className="text-2xl text-white mb-6">
                 {t('flashCards.score')}: {result.finalScore}
               </div>
               
               <div className="bg-gray-800 rounded-lg p-6 mb-8 text-left">
                 <h3 className="text-xl font-semibold text-white mb-4">
                   {t('flashCards.resultDetails')}
                 </h3>
                 <div className="space-y-2 text-gray-300">
                   <p>{t('flashCards.totalCards')}: {result.totalCards}</p>
                   <p>{t('flashCards.correctAnswers')}: {result.correctAnswers}</p>
                   <p>{t('flashCards.wrongAnswers')}: {result.wrongAnswers}</p>
                   <p>{t('flashCards.accuracy')}: {result.accuracyPercentage}%</p>
                   <p>{t('flashCards.totalTime')}: {(result.totalTimeMs / 1000).toFixed(1)}s</p>
                 </div>
               </div>
               
               <div className="space-y-4">
                 <button
                   onClick={nextRound}
                   disabled={isLoading}
                   className="w-full px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold text-xl rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   {isLoading ? t('flashAnzan.creating') : `${t('competitions.startRound')} ${currentRound + 1}`}
                 </button>
               </div>
             </div>
       </div>
     </div>
   );
   }

   // شاشة النتيجة النهائية في مودال
   if (gameState === 'result' && result) {
     const accuracyPercentage = result.accuracyPercentage || 0;
     const isGoodResult = accuracyPercentage >= 60;

     return (
         <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-3 py-4">
           <div className="w-full max-w-2xl px-4 text-center">
             <div className="mb-6 sm:mb-8">
               <div className="mb-3 text-6xl sm:mb-4 sm:text-8xl">
                 {isGoodResult ? '🎉' : '💪'}
               </div>
               
               <h2 className={`mb-3 text-2xl font-bold sm:mb-4 sm:text-4xl ${isGoodResult ? 'text-green-400' : 'text-yellow-400'}`}>
                 {isGoodResult ? t('flashCards.excellent') : t('flashCards.keepTrying')}
               </h2>
               
               <div className="mb-5 text-lg text-white sm:mb-6 sm:text-2xl">
                 {t('flashCards.score')}: {result.finalScore}
               </div>
               
               <div className="bg-gray-800 rounded-lg p-6 mb-8 text-left">
                 <h3 className="text-xl font-semibold text-white mb-4">
                   {t('flashCards.resultDetails')}
                 </h3>
                 <div className="space-y-2 text-gray-300">
                   <p>{t('flashCards.totalCards')}: {result.totalCards}</p>
                   <p>{t('flashCards.correctAnswers')}: {result.correctAnswers}</p>
                   <p>{t('flashCards.wrongAnswers')}: {result.wrongAnswers}</p>
                   <p>{t('flashCards.accuracy')}: {result.accuracyPercentage}%</p>
                   <p>{t('flashCards.totalTime')}: {(result.totalTimeMs / 1000).toFixed(1)}s</p>
                 </div>
               </div>
               
               <div className="space-y-4">
                 <button
                   onClick={resetGame}
                   className="w-full px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white font-bold text-xl rounded-lg transition-colors"
                 >
                   {t('flashCards.playAgain')}
                 </button>
               </div>
             </div>
      </div>
    </div>
  );

   }

   return null;
};

export default FlashCards;

